import React from "react";
import {
  Box,
  Flex,
  HStack,
  Image,
  Button,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Stack,
  useDisclosure,
  useColorModeValue,
  Divider,
  Text,
} from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import { HamburgerIcon, CloseIcon } from "@chakra-ui/icons";

export default function Navbar() {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Subtle uOttawa-inspired palette (tasteful)
  const uoRed = "#8F001A";
  const uoBlue = "#315369";

  const bg = useColorModeValue("rgba(255,255,255,0.92)", "rgba(20,20,20,0.75)");
  const border = useColorModeValue("rgba(0,0,0,0.10)", "rgba(255,255,255,0.10)");
  const shadow = useColorModeValue("0 10px 25px rgba(0,0,0,0.06)", "0 10px 25px rgba(0,0,0,0.30)");
  const linkColor = useColorModeValue(uoBlue, "rgba(255,255,255,0.85)");
  const hoverBg = useColorModeValue("rgba(143,0,26,0.08)", "rgba(143,0,26,0.20)");
  const activeBg = useColorModeValue("rgba(143,0,26,0.10)", "rgba(143,0,26,0.22)");
  const activeBorder = useColorModeValue("rgba(143,0,26,0.22)", "rgba(255,255,255,0.18)");

  const NAV = [
    { to: "/", label: "Home" },
    { to: "/publications", label: "Publications" },
    { to: "/awards", label: "Awards" },
    { to: "/research-grants", label: "Grants & Projects" },
    { to: "/service", label: "Service" },
    { to: "/teaching", label: "Teaching" },
  ];

  const DesktopLink = ({ to, label }) => (
    <NavLink to={to} end={to === "/"}>
      {({ isActive }) => (
        <Button
          variant="ghost"
          size="sm"
          px={4}
          py={2}
          borderRadius="999px"
          fontWeight="700"
          letterSpacing="0.2px"
          color={isActive ? uoRed : linkColor}
          bg={isActive ? activeBg : "transparent"}
          boxShadow={isActive ? `inset 0 0 0 1px ${activeBorder}` : "none"}
          _hover={{ bg: hoverBg, color: uoRed, transform: "translateY(-1px)" }}
          _active={{ transform: "translateY(0px)" }}
        >
          {label}
        </Button>
      )}
    </NavLink>
  );

  const MobileLink = ({ to, label }) => (
    <NavLink to={to} end={to === "/"}>
      {({ isActive }) => (
        <Button
          onClick={onClose}
          variant="ghost"
          justifyContent="flex-start"
          width="100%"
          borderRadius="14px"
          fontWeight="700"
          color={isActive ? uoRed : linkColor}
          bg={isActive ? activeBg : "transparent"}
          boxShadow={isActive ? `inset 0 0 0 1px ${activeBorder}` : "none"}
          _hover={{ bg: hoverBg, color: uoRed }}
          py={6}
        >
          {label}
        </Button>
      )}
    </NavLink>
  );

  return (
    <>
      {/* Fixed Navbar */}
      <Box
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="2000"
        bg={bg}
        style={{ backdropFilter: "saturate(180%) blur(10px)" }}
        borderBottom={`1px solid ${border}`}
        boxShadow={shadow}
      >
        <Flex
          align="center"
          justify="space-between"
          maxW="1200px"
          mx="auto"
          px={{ base: 4, md: 5 }}
          h="72px"
          gap={3}
        >
          {/* Left logo */}
          <Flex align="center" minW={{ base: "auto", md: "180px" }}>
            <Image
              src="uottawa engineering.jpg"
              alt="uOttawa Engineering"
              h={{ base: "42px", md: "52px" }}
              w={{ base: "140px", md: "180px" }}
              objectFit="contain"
              draggable={false}
            />
          </Flex>

          {/* Desktop nav */}
          <HStack spacing={2} display={{ base: "none", md: "flex" }} flex="1" justify="center">
            {NAV.map((item) => (
              <DesktopLink key={item.to} to={item.to} label={item.label} />
            ))}
          </HStack>

          {/* Mobile hamburger */}
          <Flex display={{ base: "flex", md: "none" }} flex="1" justify="flex-end">
            <IconButton
              aria-label="Open menu"
              icon={<HamburgerIcon />}
              onClick={onOpen}
              variant="ghost"
              borderRadius="14px"
              _hover={{ bg: hoverBg }}
            />
          </Flex>

          {/* Right logo (hidden on very small screens if needed) */}
          <Flex align="center" minW={{ base: "auto", md: "180px" }} justify="flex-end">
            <Image
              src="uottawa kanata.jpg"
              alt="uOttawa Kanata North"
              h={{ base: "42px", md: "52px" }}
              w={{ base: "140px", md: "180px" }}
              objectFit="contain"
              draggable={false}
            />
          </Flex>
        </Flex>

        {/* subtle uOttawa accent line */}
        <Box
          h="2px"
          bgGradient={`linear(to-r, transparent, ${uoRed}, transparent)`}
          opacity={0.55}
        />
      </Box>

      {/* Spacer so content doesn't go under fixed navbar */}
      <Box h="74px" />

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerHeader display="flex" alignItems="center" justifyContent="space-between">
            <Text fontWeight="800" color={uoRed}>
              Menu
            </Text>
            <IconButton
              aria-label="Close menu"
              icon={<CloseIcon />}
              onClick={onClose}
              variant="ghost"
              borderRadius="12px"
            />
          </DrawerHeader>
          <Divider />
          <DrawerBody>
            <Stack spacing={2} mt={3}>
              {NAV.map((item) => (
                <MobileLink key={item.to} to={item.to} label={item.label} />
              ))}
            </Stack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </>
  );
}